import moment from 'moment-timezone'

/**
 * Returns the long date format for the current locale.
 *
 * @param {string} [format='L'] - The format string to use. Defaults to 'L'.
 * @returns {string} The long date format for the current locale.
 */
export const momentLongDateFormat = (format = 'L') => moment.localeData().longDateFormat(format)

/**
 * Checks if a value is a valid date
 * @name isValidDate
 * @param {Date|string} value - The value to be tested.
 * @returns {boolean}
 */
export const isValidDate = (value) => {
  if (!value) return false
  return !isNaN(new Date(value))
}

/**
 * Converts a given date to a specified timezone and formats it based on the provided options.
 *
 * @param {Date|string} date - The date to be converted. Can be a Date object or a date string.
 * @param {boolean|string} [isOnlyDate=false] - Determines the format of the returned date string.
 *   - If 'time', returns only the time in 'HH:mm:ss' format.
 *   - If 'timeNoSeconds', returns only the time in 'HH:mm' format.
 *   - If true, returns only the date in 'L' format.
 *   - If false or not provided, returns the date and time in 'L HH:mm:ss' format.
 * @returns {string} The formatted date string or an empty string if the input date is invalid.
 */
export const timezoneDateConverter = (date, isOnlyDate = false) => {
  if (!date || !isValidDate(date)) return ''
  const newDate = date.includes('T') ? date.replace('T', ' ').replace('Z', '') : date

  const timeZone = sessionStorage.getItem('timeZone') // Europe/Athens || Europe/Rome || Europe/London ...
  if (!timeZone) {
    if (isOnlyDate) {
      return moment(newDate).format('L Z')
    }
    return moment(newDate).format('L HH:mm:ss Z')
  }
  const italianDate = moment.tz(newDate, 'Europe/Rome')

  if (isOnlyDate) {
    return italianDate.tz(timeZone).format('L')
  }
  return italianDate.tz(timeZone).format('L HH:mm:ss')
}

/**
 * Converts a given date to a specified timezone and formats it based on the provided options.
 *
 * @param {Date|string} date - The date to be converted. Can be a Date object or a date string.
 * @param {boolean|string} [isDate=false] - Determines the format of the returned date string.
 * @param {boolean|string} [dateWithTime=false] - Determines the format of the returned date string.
 *   - If isDate is true and dateWithTime is false, the value is managed so that it is returned in 'dd/mm/yy' format.
 *   - If dateWithTime is true, the value is managed so that it is returned in 'dd/mm/yy hh:mm:ss' format.
 *   - If isDate and dateWithTime are both false, the value is managed so that it is returned in only in 'hh:mm' format.
 * @returns {string} The formatted date string or an empty string if the input date is invalid.
 */

export const isoTimeDateConverter = (date, isDate = false, dateWithTime = false) => {
  if (!date || !isValidDate(date)) return ''
  const timeZone = sessionStorage.getItem('timeZone')
  const fallback = date.includes('T') ? date.replace('T', ' ').replace('Z', '') : date

  if (!timeZone) {
    return moment(fallback).format('L HH:mm:ss Z')
  }

  return isDate
    ? dateWithTime
      ? moment(date).tz(timeZone).format('L LTS')
      : moment(date).tz(timeZone).format('L')
    : moment(date).tz(timeZone).format('LT')
}

/**
 * Converts a date to Italian format based on the specified option.
 *
 * @param {string} date - The date to be converted.
 * @param {string} option - The conversion option. Possible values are 'start', 'end', 'isOnlyDate', or 'time'.
 * @returns {string|null} The converted date in the specified format, or null if the input date is invalid.
 */
export const italianDateConverter = (date, option) => {
  // option = 'start' || 'end' || 'isOnlyDate' || 'time'
  if (!date) return null

  const formattedDate = option === 'time' ? moment.tz(date, 'L HH:mm', moment.tz.guess()) : moment(date, 'L')

  const dateFormat = option === 'isOnlyDate' ? 'YYYY-MM-DD' : 'YYYY-MM-DDTHH:mm:ss.SSS[Z]'

  // Convert to Italian timezone and set time to midnight
  if (option === 'start') {
    const formattedStartDate = formattedDate.startOf('day')
    const italianTime = formattedStartDate.tz('Europe/Rome')
    return italianTime.isValid() ? italianTime.format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : null // Output: '2024-01-01T00:00:00.000Z'
  }
  if (option === 'end') {
    const formattedEndDate = formattedDate.endOf('day')
    const italianTime = formattedEndDate.tz('Europe/Rome')
    return italianTime.isValid() ? italianTime.format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : null // Output: '2024-01-01T23:59:59.999Z'
  }

  if (option !== 'isOnlyDate' && option !== 'time') {
    formattedDate.set({
      hour: moment().get('hour'),
      minute: moment().get('minute'),
      second: moment().get('second'),
      millisecond: moment().get('millisecond')
    })
  }

  // if not specific status, return date with current time
  const italianTime = formattedDate.tz('Europe/Rome')
  return italianTime.isValid() ? italianTime.format(dateFormat) : null // Output: '2024-01-01T15:30:59.999Z'

  // Try to parse the date in different formats
}

export const dateOutOfRange = (day, startDate, endDate) => {
  const today = moment().startOf('day')
  const thirteenMonthsAgo = moment().subtract(13, 'months').startOf('day')
  const maxRange = startDate ? moment(startDate).add(30, 'days').startOf('day') : null

  return (
    moment(day).startOf('day').isAfter(today) ||
    moment(day).startOf('day').isBefore(thirteenMonthsAgo) ||
    (startDate && moment(day).startOf('day').isAfter(maxRange)) ||
    (endDate && moment(day).startOf('day').isBefore(moment(endDate).subtract(30, 'days').startOf('day')))
  )
}

/**
 * Converts a date from UTC to the terminal's time zone and formats it.
 * @param {Date} date - The date to be converted.
 * @param {string} format - The format in which the date should be displayed. Defaults to 'L' if not provided.
 * @returns {string} The formatted date in the terminal's time zone.
 */
export const dateFromUTCtoTerminalTimeZone = (date, format) => {
  if (!date) return ''
  const timeZone = sessionStorage.getItem('timeZone') // Europe/Athens || Europe/Rome || Europe/London ...
  return moment.tz(date, timeZone).format(format ?? 'L')
}

/**
 * Combines a date and a time into a single ISO 8601 string in UTC.
 * @param {string|Date} date - The date to be combined (e.g., '2024-11-27').
 * @param {string|Date} time - The time to be combined (e.g., '14:30:00').
 * @returns {string} The combined date and time in ISO 8601 format (UTC).
 */
export const joinDateAndTime = (date, time) =>
  moment(date)
    .set({
      hour: moment(time).hour(),
      minute: moment(time).minute(),
      second: moment(time).second()
    })
    .toISOString()

/**
 * Detects errors in time input.
 * @param {Object} customDate - An object containing `from` and `to` date values.
 * @param {Object} customTime - An object containing `from` and `to` time values.
 * @returns {Object} An object indicating if there is an error with the start and/or end time.
 *                   { start: true, end: true } if an error is detected,
 *                   { start: false, end: false } otherwise.
 */
export const timeErrorDetect = (customDate, customTime) => {
  const { from: firstDay, to: lastDay } = customDate || {}
  const { from: fromTime, to: toTime } = customTime || {}

  if (
    firstDay &&
    lastDay &&
    moment(firstDay).isSame(moment(lastDay), 'day') &&
    moment(fromTime, 'HH:mm').isAfter(moment(toTime, 'HH:mm'))
  ) {
    return { start: true, end: true }
  }

  return { start: false, end: false }
}

/**
 * Converts the number of days to a different period.
 * @param {number} days - The number of days to convert.
 * @param {string} period - The period to convert to. Possible values are 'WEEK', 'MONTH'.
 * @returns {number} - The converted number of days.
 */
export const daysConverter = (days, period) => {
  if (period === 'WEEK') return days * 7
  if (period === 'MONTH') return days * 30
  return days
}

/**
 * calculates the difference between two dates, in hours or days
 * @name getDateDiff
 * @param {string | object} dateA - The first date to compare.
 * @param {string | object} dateB - The second date to compare.
 * @param {boolean} format - If true, returns the difference in days; otherwise, returns in hours.
 * @returns {string}
 */
export const getDateDiff = (dateA, dateB, format = 'days') => {
  const startDate = moment(dateA).startOf('day')
  const endDate = moment(dateB).startOf('day')
  if (!startDate.isValid() || !endDate.isValid()) {
    return ''
  }
  return endDate.diff(startDate, format)
}

export const datePickerEternalMaxDate = '2100-01-01'

export const minDate = moment().subtract(13, 'months').startOf('day').format('YYYY-MM-DD') // format 'YYYY-MM-DD' for date picker component
export const getMaxDate = () => moment().format('YYYY-MM-DD')
export let maxDate = getMaxDate()
export const outOfRangeCalendar = (date) => {
  if (!date) {
    return getMaxDate()
  }
  
  const today = moment().startOf("day")
  const selected = moment(date, momentLongDateFormat()).startOf("day")
  const limit = moment().subtract(30, "days").startOf("day")

  if (selected.isAfter(limit)) {
    return maxDate = today.format("YYYY-MM-DD")
  } else {
    return maxDate = selected.add(30, "days").format("YYYY-MM-DD")
  }
}
// @generator utility:method
